import { useState } from 'react';
import { Users, UserPlus, Trash2, ShieldCheck, ShieldAlert } from 'lucide-react';
import { useUsers, useCurrentUser } from '../hooks/useApi';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { api } from '../api/client';
import { useQueryClient } from '@tanstack/react-query';
import { AllowedUser } from '../types/api';

const SUPER_ADMIN = 'michal@cloudteam.pl';

export function UsersPage() {
  const users = useUsers();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<'user' | 'marketing' | 'admin'>('user');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = currentUser.data?.isAdmin ?? false;

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    setLoading(true);
    setError(null);
    try {
      await api.addUser(email, newRole);
      setNewEmail('');
      setNewRole('user');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (email: string) => {
    if (!confirm(`Usunac ${email}?`)) return;
    try {
      await api.removeUser(email);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleRole = async (user: AllowedUser) => {
    const roleOrder: Array<'user' | 'marketing' | 'admin'> = ['user', 'marketing', 'admin'];
    const currentIdx = roleOrder.indexOf(user.role);
    const newRole = roleOrder[(currentIdx + 1) % roleOrder.length];
    try {
      await api.updateUserRole(user.email, newRole);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Brak dostepu</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Users className="h-6 w-6 text-brand-400" />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Uzytkownicy</h1>
      </div>

      {/* Add user form */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Dodaj uzytkownika
        </h2>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Email</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="user@cloudteam.pl"
              className="input-field w-full"
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Rola</label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as 'user' | 'marketing' | 'admin')}
              className="input-field"
            >
              <option value="user">User</option>
              <option value="marketing">Marketing</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button
            onClick={handleAdd}
            disabled={loading || !newEmail.includes('@')}
            className="btn-primary disabled:opacity-50"
          >
            {loading ? '...' : 'Dodaj'}
          </button>
        </div>
        {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      </div>

      {/* Users list */}
      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
          Uprawnieni uzytkownicy ({users.data?.length ?? 0})
        </h2>

        {users.isLoading ? (
          <LoadingSpinner size="sm" />
        ) : users.error ? (
          <ErrorAlert message={users.error.message} onRetry={() => users.refetch()} />
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {users.data?.map((user) => {
              const isSuperAdmin = user.email === SUPER_ADMIN;
              return (
                <div key={user.email} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-full ${
                      user.role === 'admin' ? 'bg-amber-100 dark:bg-amber-900/30' :
                      user.role === 'marketing' ? 'bg-purple-100 dark:bg-purple-900/30' :
                      'bg-gray-100 dark:bg-gray-800'
                    }`}>
                      {user.role === 'admin' ? (
                        <ShieldCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      ) : user.role === 'marketing' ? (
                        <ShieldCheck className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <Users className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.email}</p>
                      <p className="text-xs text-gray-500">
                        {isSuperAdmin ? 'Super Admin' : user.role === 'admin' ? 'Admin' : user.role === 'marketing' ? 'Marketing' : 'User'}
                        {user.addedBy && !isSuperAdmin && ` · dodany przez ${user.addedBy}`}
                      </p>
                    </div>
                  </div>

                  {!isSuperAdmin && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleRole(user)}
                        className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title={user.role === 'admin' ? 'Zmien na User' : 'Zmien na Admin'}
                      >
                        <ShieldAlert className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRemove(user.email)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        title="Usun"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
